// Monaco CodeActionProvider for navigational-features Phase 1.
//
// Slice 4 ships only the registration surface — `provideCodeActions` walks
// the registry, but Slice 4's registry is empty until capability slices
// 10–14 register their fix factories. With an empty registry the provider
// returns `[]` (no lightbulb), which matches Monaco's "nothing to fix" UX.
//
// The registry is intentionally Monaco-free; this module is the only place
// `CodeActionEdit.textEdits[]` are adapted into `monaco.languages.WorkspaceEdit`.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 4.

import type { editor, IRange, languages } from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

import type {
  CodeActionContext,
  CodeActionDiagnostic,
  CodeActionEdit,
  CodeActionId,
  CodeActionKindHint,
  CodeActionRegistryEntry,
  SqlPosition,
  SqlRange,
} from '../types';
import { trackNavFeature } from '../analytics/trackNavFeature';

import type { CodeActionRegistry } from './codeActionRegistry';

/** Stable command id for the post-selection tracking hook.
 *  Monaco runs this AFTER applying the edit, signalling real user invocation
 *  (not just lightbulb-compute). */
export const NAV_CODE_ACTION_TRACK_COMMAND_ID = 'navfeatures.trackCodeAction';

/** Mirrors `DefinitionProviderDeps` — getter-pattern keeps the registration
 *  site stable across capability slices. */
export type DocumentIdGetter = () => string | null;
export type ConnectionIdGetter = () => string | null;
export type DatabaseGetter = () => string | null;

export type CodeActionProviderDeps = {
  readonly engineId: DatabaseEngineId;
  readonly getDocumentId: DocumentIdGetter;
  /** Capability slices 11–14 read these to scope schema-aware lookups (e.g.
   *  Slice 11's OE-tree walk for ambiguous-column candidates). Optional —
   *  Slice 4 wired them as `() => null` until capability slices need them. */
  readonly getConnectionId?: ConnectionIdGetter;
  readonly getDatabase?: DatabaseGetter;
  readonly registry: CodeActionRegistry;
};

const KIND_HINT_TO_MONACO: Record<CodeActionKindHint, string> = {
  quickfix: 'quickfix',
  refactor: 'refactor',
  'refactor.extract': 'refactor.extract',
};

/** Minimal Monaco surface needed to register the tracking command. */
type MonacoForCommandRegistration = {
  readonly editor: {
    readonly registerCommand: (
      id: string,
      handler: (_accessor: unknown, ...args: unknown[]) => void,
    ) => { dispose: () => void };
  };
};

/** Tracks whether the command has already been registered in this page-lifetime.
 *  Monaco's `registerCommand` is global and last-write-wins — re-registering
 *  the same id REPLACES the handler (it is NOT a no-op). We guard so repeated
 *  mounts/HMR don't re-register (which would also leak the prior disposable). */
let _commandRegistered = false;

/** Reset the registration guard — for testing only. */
export function _resetCommandRegisteredForTesting(): void {
  _commandRegistered = false;
}

/** Register the post-selection tracking command with Monaco ONCE.
 *  Must be called from the Monaco registration effect (which has a live
 *  `monaco` instance). Safe to call on every mount — the guard prevents
 *  double-registration. */
export function registerNavCodeActionTrackingCommand(
  monaco: MonacoForCommandRegistration,
): void {
  if (_commandRegistered) return;
  _commandRegistered = true;
  monaco.editor.registerCommand(
    NAV_CODE_ACTION_TRACK_COMMAND_ID,
    (_accessor: unknown, action: unknown) => {
      trackNavFeature({
        name: 'feature:navfeatures_code_action',
        props: { action: action as CodeActionId },
      });
    },
  );
}

export function createCodeActionProvider(
  deps: CodeActionProviderDeps,
): languages.CodeActionProvider {
  return {
    provideCodeActions: (model, range, context) => {
      const entries = deps.registry.list();
      if (entries.length === 0) {
        return { actions: [], dispose: () => {} };
      }

      // engineId / document-id captured for forward-compat; capability
      // slices 10–14 will read these inside their `canApply` / `buildEdits`
      // factories. Slice 4's contract is just the wiring.
      //
      // A `getResolverService` dep used to live alongside `getDocumentId`
      // here — deleted (Plan 19 T9, decision 10): shipped capability slices
      // 10–14 never read it (both providers only ever `void`-called the
      // getter). Reviving per-connection resolver-service threading here
      // would be wiring a dep to zero readers.
      const ctx = buildContext(model, range, context, deps);
      void deps.getDocumentId;

      const actions: languages.CodeAction[] = [];
      for (const entry of entries) {
        if (!entry.canApply(ctx)) continue;
        const edits = entry.buildEdits(ctx);
        for (const edit of edits) {
          actions.push(adaptToMonacoAction(model, entry, edit));
        }
      }

      return { actions, dispose: () => {} };
    },
  };
}

function buildContext(
  model: editor.ITextModel,
  range: IRange,
  ctx: languages.CodeActionContext,
  deps: CodeActionProviderDeps,
): CodeActionContext {
  // Monaco's IRange is 1-indexed; SqlRange / SqlPosition are 0-indexed (per
  // sql-grammar's model convention — see InlineVqeToolbar / editAsSql.ts).
  const sqlRange: SqlRange = {
    startOffset: model.getOffsetAt({
      lineNumber: range.startLineNumber,
      column: range.startColumn,
    }),
    endOffset: model.getOffsetAt({
      lineNumber: range.endLineNumber,
      column: range.endColumn,
    }),
    startLine: range.startLineNumber - 1,
    startColumn: range.startColumn - 1,
    endLine: range.endLineNumber - 1,
    endColumn: range.endColumn - 1,
  };
  // Cursor convention (matches the resolver-service): the active position is
  // the END of the selection range when one exists, else the single point.
  const cursor: SqlPosition = {
    offset: sqlRange.endOffset,
    line: range.endLineNumber - 1,
    column: range.endColumn - 1,
  };
  const diagnosticCodes: string[] = [];
  const diagnostics: CodeActionDiagnostic[] = [];
  for (const marker of ctx.markers ?? []) {
    const code = markerCode(marker.code);
    if (code === null) continue;
    diagnosticCodes.push(code);
    // A fix that is ATTACHED to its diagnostic (rather than re-deriving the
    // problem) needs the marker's message + span, not just its code.
    const markerRange = markerSqlRange(model, marker);
    if (markerRange) {
      diagnostics.push({ code, message: marker.message ?? '', range: markerRange });
    }
  }
  return {
    engineId: deps.engineId,
    sourceText: model.getValue(),
    range: sqlRange,
    cursor,
    diagnosticCodes,
    diagnostics,
    connectionId: deps.getConnectionId?.() ?? null,
    database: deps.getDatabase?.() ?? null,
  };
}

function markerCode(code: editor.IMarkerData['code']): string | null {
  if (typeof code === 'string') return code;
  if (code && typeof code === 'object' && 'value' in code) {
    const codeValue = (code as { value: unknown }).value;
    return typeof codeValue === 'string' ? codeValue : String(codeValue);
  }
  return null;
}

/** Monaco marker positions (1-indexed) → the provider's 0-indexed `SqlRange`.
 *  `null` when the marker carries no usable position (defensive — stubbed
 *  markers in unit tests). */
function markerSqlRange(
  model: editor.ITextModel,
  marker: editor.IMarkerData,
): SqlRange | null {
  const { startLineNumber, startColumn, endLineNumber, endColumn } = marker;
  const positions = [startLineNumber, startColumn, endLineNumber, endColumn];
  if (positions.some((n) => typeof n !== 'number' || !Number.isFinite(n))) return null;
  return {
    startOffset: model.getOffsetAt({ lineNumber: startLineNumber, column: startColumn }),
    endOffset: model.getOffsetAt({ lineNumber: endLineNumber, column: endColumn }),
    startLine: startLineNumber - 1,
    startColumn: startColumn - 1,
    endLine: endLineNumber - 1,
    endColumn: endColumn - 1,
  };
}

function adaptToMonacoAction(
  model: editor.ITextModel,
  entry: CodeActionRegistryEntry,
  edit: CodeActionEdit,
): languages.CodeAction {
  // Per-edit `description` becomes the Monaco lightbulb title — lets multi-
  // candidate fixes (Slice 11's "Qualify as users.email" / "Qualify as
  // accounts.email") render with distinct labels. Falls back to entry-level
  // `title` when an edit ships an empty description (defensive).
  return {
    title: edit.description.length > 0 ? edit.description : entry.title,
    kind: KIND_HINT_TO_MONACO[entry.kind],
    edit: {
      edits: edit.textEdits.map((textEdit) => ({
        resource: model.uri,
        textEdit: {
          range: sqlRangeToMonaco(textEdit.range),
          text: textEdit.newText,
        },
        versionId: model.getVersionId(),
      })),
    },
    // Attach the tracking command so Monaco fires it AFTER the user selects
    // and Monaco applies the edit. This is the correct invocation signal —
    // `provideCodeActions` runs on every cursor-move (lightbulb compute) and
    // must NOT fire analytics.
    command: {
      id: NAV_CODE_ACTION_TRACK_COMMAND_ID,
      title: '',
      arguments: [edit.id],
    },
  };
}

function sqlRangeToMonaco(range: SqlRange): IRange {
  return {
    startLineNumber: range.startLine + 1,
    startColumn: range.startColumn + 1,
    endLineNumber: range.endLine + 1,
    endColumn: range.endColumn + 1,
  };
}
